import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AUTH_TOKEN } from '../../config';
import { useHeliTour, usePilots, pilotFor } from '../../city/heliTour';
import { exitFlight } from '../../city/heliActions';
import { heliSay, heliHold } from '../../services/worldSocket';
import { takeAudio, releaseAudio, onAudio } from '../../services/audioPool';
import { audioEngine } from '../../services/audioEngine';
import useAvatarVoiceChat from '../../hooks/useAvatarVoiceChat';

// TALK TO THE PILOT IN THE AIR (2026-11-04). Khari: "it may be interesting to be
// able to interrupt them during the flight … the same DJ rules", then: voice too;
// "cut in naturally, and let her flow, even if it interrupts the flight … We can
// also extend flights by up to 30 seconds if needed"; four answers a flight; and
// "allow the main user to end their flight if solo, and exit the flight if not solo."
//
//   ALONE AND SIGNED IN   the mic opens a live two-way call with her (her own voice
//                         agent, API voice-session `surface: 'heli'`); what you
//                         type goes into the same call. Each answer is counted
//                         (`heli_hold answered`) and, while she talks, the
//                         helicopter hovers (`heli_hold`), so the tour waits.
//   OTHERWISE             (several aboard, a guest, or the call refused) what you
//                         say, typed or spoken (this browser's speech-to-text),
//                         goes to the cabin (`heli_say`, API sockets/heliCabin:
//                         the radio DJ's one-or-many rules). Her answer comes to
//                         everyone aboard (`heli_pilot`) in her voice, and the
//                         server hovers the helicopter for it.
// Either way the tour line under her stops (`pilotTalking`: HeliPanel's tour
// player cuts it) and picks up at the next stop.
// The text box is never disabled (the platform rule): only the send is refused.

export const CAP_ANSWERS = 4;   // the API's heliCabin CAP_ANSWERS
const SR = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition || null) : null;
const HOLD_EVERY_MS = 3500;     // while her live call is speaking, ask for the hover again this often
const HOLD_ASK_MS = 6000;       // …to last this long from now
const sayMs = (t) => Math.round((String(t || '').split(/\s+/).filter(Boolean).length / 2.6) * 1000) + 1500;

const GOLD = '#f2b632';
const pillBtn = (on = true) => ({ cursor: on ? 'pointer' : 'default', opacity: on ? 1 : 0.5, borderRadius: 999, padding: '7px 12px', border: `1px solid ${GOLD}88`, background: 'transparent', color: GOLD, font: '700 12px system-ui', whiteSpace: 'nowrap' });

/** Plays her recorded answers (`heli_pilot`) for this pad, and says when she is talking. */
function usePilotAnswers(padId) {
  const says = useHeliTour((s) => s.pilotSays);
  const el = useRef(null);
  useEffect(() => {
    if (!says || says.pad !== padId) return undefined;
    useHeliTour.setState({ caption: { text: says.text, kind: 'answer', pilot: true } });
    if (!says.audioUrl) {
      useHeliTour.setState({ pilotTalking: true });
      const t = setTimeout(() => useHeliTour.setState({ pilotTalking: false }), sayMs(says.text));
      return () => { clearTimeout(t); useHeliTour.setState({ pilotTalking: false }); };
    }
    const a = takeAudio(says.audioUrl);
    el.current = a;
    const done = () => { if (el.current === a) { releaseAudio(a); el.current = null; audioEngine.duck(false); useHeliTour.setState({ pilotTalking: false }); } };
    onAudio(a, 'ended', done);
    onAudio(a, 'error', done);
    useHeliTour.setState({ pilotTalking: true });
    audioEngine.duck(true);
    // Refused: asked again a few times before giving up (services/audioPool keepSounding: an iPhone can refuse a pooled player's one play()).
    let tries = 0;
    const play = () => a.play().catch(() => {
      if (el.current !== a) return;
      if (++tries > 3) { done(); return; }
      setTimeout(() => { if (el.current === a && a.paused && !a.ended) play(); }, 700);
    });
    play();
    return () => done();
  }, [says, padId]);
}

export default function HeliTalk({ padId, p, wrap }) {
  const pilot = usePilots((s) => pilotFor(padId, s));
  const offline = useHeliTour((s) => s.offline);
  const tour = useHeliTour((s) => s.tour);
  const says = useHeliTour((s) => s.pilotSays);
  const first = pilot?.name?.split(' ')[0] || 'the pilot';
  const seats = p?.seats || [];
  // An artist riding along (API city/HeliRideService) is aboard but not on the headset (Khari:
  // "limit it to the pilot for now"): the talk rules count PEOPLE, the button counts everyone
  // (with them aboard, getting out leaves the flight to fly on: Exit).
  const solo = seats.filter((s) => !s.avatarId).length <= 1;
  const alone = seats.length <= 1;
  const member = !!AUTH_TOKEN;
  const [text, setText] = useState('');
  const [note, setNote] = useState(null);
  const [liveAnswers, setLiveAnswers] = useState(0);
  const [liveRefused, setLiveRefused] = useState(false);
  const [hearing, setHearing] = useState(false);
  const recRef = useRef(null);
  const flightKey = `${padId}:${p?.departAt || ''}`;
  usePilotAnswers(padId);

  // Her answers so far this flight: the cabin's count (recorded) or the call's.
  const recorded = says && says.pad === padId ? says.n : 0;
  const answered = Math.max(recorded, liveAnswers);
  const left = Math.max(0, CAP_ANSWERS - answered);
  const capped = left <= 0;

  // ── her live call (alone, signed in) ──
  const onReply = useCallback((t) => {
    setLiveAnswers((n) => n + 1);
    heliHold(sayMs(t), true).catch?.(() => {});
  }, []);
  const call = useAvatarVoiceChat({ avatarId: pilot?.avatarId || null, surface: 'heli', heli: padId, onReply });
  const liveOk = solo && member && !offline && !!pilot && !liveRefused;
  useEffect(() => { if (call.error && !call.active) setLiveRefused(true); }, [call.error, call.active]);
  // The call is the conversation: the tour stays stopped while it is open (you cut in
  // on her; her line would also reach your mic), and the helicopter hovers while she speaks.
  useEffect(() => {
    useHeliTour.setState({ pilotTalking: !!call.active });
    if (!call.active || !call.speaking) return undefined;
    heliHold(HOLD_ASK_MS).catch?.(() => {});
    const iv = setInterval(() => heliHold(HOLD_ASK_MS).catch?.(() => {}), HOLD_EVERY_MS);
    return () => clearInterval(iv);
  }, [call.active, call.speaking]);
  useEffect(() => { if (call.caption && call.active) useHeliTour.setState({ caption: { text: call.caption, kind: 'answer', pilot: true } }); }, [call.caption, call.active]);
  // Her four answers given: the call ends once she has finished the last.
  useEffect(() => { if (call.active && capped && !call.speaking) call.end(); }, [call.active, capped, call.speaking]); // eslint-disable-line react-hooks/exhaustive-deps
  // A new flight starts the count again; landing or getting out ends the call.
  useEffect(() => { setLiveAnswers(0); setLiveRefused(false); setNote(null); }, [flightKey]);
  useEffect(() => () => { try { call.end(); } catch { /* gone */ } useHeliTour.setState({ pilotTalking: false }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (raw) => {
    const t = String(raw || '').trim();
    if (!t) return;
    if (capped) { setNote(`That's ${first}'s ${CAP_ANSWERS} answers for this flight. She's flying now; ask her more on the ground.`); return; }
    setNote(null);
    setText('');
    if (call.active && call.say(t)) return;   // into the live call
    const r = await heliSay(padId, t, tour?.below || null, !!tour?.playing);
    if (r && r.ok === false && !r.offline) setNote(r.reason === 'capped' ? `That's ${first}'s ${CAP_ANSWERS} answers for this flight.` : r.reason === 'slow_down' ? 'One at a time.' : "That didn't reach her. Try again.");
    else if (r?.offline) setNote(`${first} can't hear the cabin right now.`);
  };

  // The browser's own speech-to-text (several aboard, a guest, or no call): what you say is sent as you finish.
  const hear = () => {
    if (!SR || hearing) { try { recRef.current?.stop(); } catch { /* not listening */ } return; }
    try {
      const r = new SR();
      r.lang = 'en-US'; r.interimResults = true; r.maxAlternatives = 1; r.continuous = false;
      let heard = '';
      r.onresult = (e) => { heard = Array.from(e.results).map((x) => x[0]?.transcript || '').join(' ').trim(); setText(heard); };
      r.onend = () => { setHearing(false); recRef.current = null; if (heard) send(heard); };
      r.onerror = () => { setHearing(false); recRef.current = null; };
      recRef.current = r;
      setHearing(true);
      r.start();
    } catch { setHearing(false); }
  };
  const mic = () => {
    if (capped) { setNote(`That's ${first}'s ${CAP_ANSWERS} answers for this flight.`); return; }
    if (liveOk) { call.toggleTalk(); return; }
    hear();
  };
  const micOn = (liveOk && call.active) || hearing;
  const canMic = liveOk || !!SR;
  const status = call.active
    ? (call.phase === 'connecting' ? `Getting ${first} on the headset…` : call.phase === 'speaking' ? `${first} is talking` : call.phase === 'listening' ? `${first} is listening` : `On the headset with ${first}`)
    : hearing ? 'Listening…'
      : capped ? `${first} has answered ${CAP_ANSWERS} times · she's flying now`
        : `Talk to ${first} · ${left} answer${left === 1 ? '' : 's'} left this flight`;

  const out = async () => { try { call.end(); } catch { /* none open */ } await exitFlight(); };

  return (
    <div style={{ ...wrap, zIndex: 75, pointerEvents: 'auto' }}>
      <div style={{ background: 'rgba(8,12,20,0.82)', border: `1px solid ${GOLD}44`, borderRadius: 14, padding: '8px 10px', color: '#eef1f5', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
          <span style={{ font: '600 11.5px system-ui', color: GOLD, opacity: 0.9 }}>{status}</span>
          <button type="button" style={pillBtn()} onClick={out}>{alone ? 'End flight' : 'Exit flight'}</button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(text); }} style={{ display: 'flex', gap: 6 }}>
          {canMic && (
            <button type="button" onClick={mic} aria-pressed={micOn} aria-label={micOn ? 'Stop' : `Talk to ${first}`}
              style={{ ...pillBtn(), padding: '7px 11px', background: micOn ? GOLD : 'transparent', color: micOn ? '#141821' : GOLD }}>
              {micOn ? '■' : '🎙'}
            </button>
          )}
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder={`Say something to ${first}…`} maxLength={240}
            style={{ flex: 1, minWidth: 0, borderRadius: 999, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.06)', color: '#fff', padding: '8px 12px', font: '500 14px system-ui', outline: 'none' }} />
          <button type="submit" style={pillBtn(!!text.trim())}>Send</button>
        </form>
        {note && <div style={{ fontSize: 12, color: '#ffcfa8', marginTop: 6 }}>{note}</div>}
      </div>
    </div>
  );
}
